import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  Sparkles,
  ScanLine,
  Palette,
  Gauge,
  Users,
  BookOpen,
  Settings,
  Plus,
  ListChecks,
  FileText,
  PenLine,
  GraduationCap,
  Building2,
  MessageSquare,
  Wand2,
  Library,
  Languages,
  Presentation,
  Clock,
  ClipboardList,
} from "lucide-react";
import { getJSON, setJSON } from "@/lib/safe-storage";

const Ctx = createContext(null);
const RECENT_KEY = "teebee.cmd.recents.v1";
const MAX_RECENTS = 6;

// NOTE: stores last-used command IDs only — non-sensitive UI breadcrumbs.
function readRecents() {
  return getJSON(RECENT_KEY, []);
}
function pushRecent(item) {
  const cur = readRecents().filter((r) => r.id !== item.id);
  const next = [item, ...cur].slice(0, MAX_RECENTS);
  setJSON(RECENT_KEY, next);
}

export function CommandPaletteProvider({ children }) {
  const [open, setOpen] = useState(false);

  const openFn = useCallback(() => setOpen(true), []);
  const closeFn = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ctxValue = useMemo(() => ({ open: openFn, close: closeFn }), [openFn, closeFn]);

  return (
    <Ctx.Provider value={ctxValue}>
      {children}
      <CommandPaletteDialog open={open} setOpen={setOpen} />
    </Ctx.Provider>
  );
}

export const useCommandPalette = () => useContext(Ctx);

/* ---------- Command catalog ---------- */
const NAV_COMMANDS = [
  { id: "nav-dashboard",      label: "Dashboard",             icon: LayoutDashboard, path: "/dashboard",          keywords: "home overview" },
  { id: "nav-tfa",            label: "Teach from anything",   icon: Wand2,           path: "/teach-from-anything",keywords: "flagship workflow generate everything" },
  { id: "nav-lessons",        label: "Lessons",               icon: Library,         path: "/lessons",            keywords: "workspace canvas saved" },
  { id: "nav-teaching",       label: "AI Teaching Assistant", icon: Sparkles,        path: "/teaching-assistant", keywords: "ai chat" },
  { id: "nav-ocr",            label: "Document OCR",          icon: ScanLine,        path: "/ocr",                keywords: "scan textbook handwriting" },
  { id: "nav-content",        label: "Content Studio",        icon: Palette,         path: "/content-studio",     keywords: "presentation ppt poster" },
  { id: "nav-assessment",     label: "Assessment Engine",     icon: Gauge,           path: "/assessment",         keywords: "grade rubric feedback" },
  { id: "nav-attendance",     label: "Smart Attendance",      icon: Users,           path: "/attendance",         keywords: "class roster present" },
  { id: "nav-parent-comms",   label: "Parent Communication",  icon: MessageSquare,   path: "/parent-comms",       keywords: "message whatsapp email" },
  { id: "nav-knowledge",      label: "Knowledge Hub",         icon: BookOpen,        path: "/knowledge-hub",      keywords: "search library reference" },
  { id: "nav-institution",    label: "Institution",           icon: Building2,       path: "/institution",        keywords: "school admin invitation" },
  { id: "nav-settings",       label: "Settings",              icon: Settings,        path: "/settings",           keywords: "profile preferences" },
];

const ACTION_COMMANDS = [
  { id: "act-tfa",          label: "Run · Teach from anything",   icon: Wand2,         path: "/teach-from-anything",                 keywords: "one shot workflow" },
  { id: "act-lesson",       label: "Generate · Lesson Plan",      icon: PenLine,       path: "/teaching-assistant?tool=lesson_plan", keywords: "lesson plan" },
  { id: "act-mcq",          label: "Generate · MCQs",             icon: ListChecks,    path: "/teaching-assistant?tool=mcq",         keywords: "multiple choice quiz" },
  { id: "act-worksheet",    label: "Generate · Worksheet",        icon: FileText,      path: "/teaching-assistant?tool=worksheet",   keywords: "homework worksheet" },
  { id: "act-rubric",       label: "Generate · Rubric",           icon: GraduationCap, path: "/teaching-assistant?tool=rubric",      keywords: "scoring criteria" },
  { id: "act-homework",     label: "Generate · Homework",         icon: ClipboardList, path: "/teaching-assistant?tool=homework",    keywords: "homework practice" },
  { id: "act-ppt",          label: "Create · Presentation",       icon: Presentation,  path: "/content-studio",                       keywords: "ppt slides" },
  { id: "act-translate",    label: "Translate · Lesson content",  icon: Languages,     path: "/teaching-assistant?tool=lesson_plan", keywords: "language convert" },
  { id: "act-scan",         label: "Scan · Document",             icon: ScanLine,      path: "/ocr",                                  keywords: "ocr camera" },
  { id: "act-parent",       label: "Compose · Parent Message",    icon: MessageSquare, path: "/parent-comms",                         keywords: "parent letter" },
  { id: "act-newclass",     label: "Create · New class",          icon: Plus,          path: "/attendance?action=new",                keywords: "class roster" },
];

const ALL_BY_ID = Object.fromEntries([...NAV_COMMANDS, ...ACTION_COMMANDS].map((c) => [c.id, c]));

function CommandPaletteDialog({ open, setOpen }) {
  const navigate = useNavigate();
  const [recents, setRecents] = useState(() => readRecents());

  useEffect(() => {
    if (open) setRecents(readRecents());
  }, [open]);

  const run = (cmd) => {
    setOpen(false);
    pushRecent({ id: cmd.id, label: cmd.label });
    navigate(cmd.path);
  };

  const recentCommands = recents.map((r) => ALL_BY_ID[r.id]).filter(Boolean);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages, actions, AI tools…" data-testid="command-input" />
      <CommandList className="max-h-[60vh]">
        <CommandEmpty>No results. Try &ldquo;lesson&rdquo;, &ldquo;scan&rdquo; or &ldquo;translate&rdquo;.</CommandEmpty>

        {recentCommands.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recentCommands.map((c) => {
                const Icon = c.icon;
                return (
                  <CommandItem
                    key={`recent-${c.id}`}
                    value={`recent ${c.label} ${c.keywords || ""}`}
                    onSelect={() => run(c)}
                    data-testid={`cmd-recent-${c.id}`}
                  >
                    <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                    {c.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Navigate">
          {NAV_COMMANDS.map((c) => {
            const Icon = c.icon;
            return (
              <CommandItem
                key={c.id}
                value={`${c.label} ${c.keywords || ""}`}
                onSelect={() => run(c)}
                data-testid={`cmd-${c.id}`}
              >
                <Icon className="h-4 w-4 mr-2" /> {c.label}
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Quick AI Actions">
          {ACTION_COMMANDS.map((c) => {
            const Icon = c.icon;
            return (
              <CommandItem
                key={c.id}
                value={`${c.label} ${c.keywords || ""}`}
                onSelect={() => run(c)}
                data-testid={`cmd-${c.id}`}
              >
                <Icon className="h-4 w-4 mr-2" /> {c.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
